import { useState, useEffect, useRef } from "react";
import { Search, QrCode, Wallet, Stamp, Check, Plus, Minus, LogOut, Users, X, Camera, Settings, Baby, User, Maximize2, Minimize2 } from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPStaffPortal = () => {
  const [loggedIn, setLoggedIn] = useState(false);
  const [pin, setPin] = useState("");
  const [staffPin, setStaffPin] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [settings, setSettings] = useState({ price_per_child: 7.50, price_per_adult: 0, loyalty_target: 10, reward_value: 7.50 });
  const [settingsForm, setSettingsForm] = useState({});
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  // POS state
  const [posAccount, setPosAccount] = useState(null);
  const [numChildren, setNumChildren] = useState(1);
  const [numAdults, setNumAdults] = useState(0);
  const [posStep, setPosStep] = useState("select"); // select, confirm, success
  const [checkinResult, setCheckinResult] = useState(null);
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [walletAmount, setWalletAmount] = useState("");
  const [walletDesc, setWalletDesc] = useState("");

  const html5QrRef = useRef(null);
  const lastScannedRef = useRef("");

  // Fullscreen
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen().catch(() => {});
    }
  };

  // QR Scanner
  useEffect(() => {
    if (!loggedIn || posAccount) return;
    let scanner = null;
    const onScan = async (code) => {
      const cleanCode = code.trim().toUpperCase();
      try {
        const res = await axios.get(`${API}/stayandplay/staff/qr/${encodeURIComponent(cleanCode)}?pin=${staffPin}`);
        if (res.data && res.data.id) {
          toast.success(`Found: ${res.data.full_name}`);
          openPOS(res.data.id);
        }
      } catch { toast.error("QR code not recognised"); }
    };
    const startScanner = async () => {
      try {
        await new Promise(r => setTimeout(r, 600));
        const el = document.getElementById("sp-qr-reader");
        if (!el) return;
        scanner = new Html5Qrcode("sp-qr-reader");
        html5QrRef.current = scanner;
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 200, height: 200 }, aspectRatio: 1.0 },
          (decodedText) => {
            if (decodedText === lastScannedRef.current) return;
            lastScannedRef.current = decodedText;
            setTimeout(() => { lastScannedRef.current = ""; }, 3000);
            onScan(decodedText);
          },
          () => {}
        );
      } catch (err) { console.log("QR scanner:", err); }
    };
    startScanner();
    return () => {
      if (scanner && scanner.isScanning) scanner.stop().catch(() => {});
    };
  }, [loggedIn, posAccount, staffPin]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogin = async () => {
    try {
      await axios.post(`${API}/stayandplay/staff/login`, { pin });
      setLoggedIn(true);
      setStaffPin(pin);
      const s = await axios.get(`${API}/stayandplay/settings`);
      setSettings(s.data);
      setSettingsForm(s.data);
    } catch { toast.error("Invalid PIN"); }
  };

  const search = async (q) => {
    setSearchQuery(q);
    if (q.length < 2) { setSearchResults([]); return; }
    try {
      const res = await axios.get(`${API}/stayandplay/staff/search?q=${encodeURIComponent(q)}&pin=${staffPin}`);
      setSearchResults(res.data);
    } catch { setSearchResults([]); }
  };

  const openPOS = async (accountId) => {
    try {
      const res = await axios.get(`${API}/stayandplay/staff/account/${accountId}?pin=${staffPin}`);
      setPosAccount(res.data);
      setNumChildren(1);
      setNumAdults(0);
      setPosStep("select");
      setCheckinResult(null);
      setSearchResults([]);
      setSearchQuery("");
      // Stop scanner
      if (html5QrRef.current && html5QrRef.current.isScanning) {
        html5QrRef.current.stop().catch(() => {});
      }
    } catch { toast.error("Failed to load account"); }
  };

  const closePOS = () => {
    setPosAccount(null);
    setPosStep("select");
    setCheckinResult(null);
  };

  const total = (numChildren * settings.price_per_child) + (numAdults * (settings.price_per_adult || 0));
  const walletBalance = posAccount?.wallet_balance || 0;
  const walletDeduction = Math.min(walletBalance, total);
  const remaining = Math.max(0, total - walletDeduction);

  const doCheckin = async (paymentMethod) => {
    try {
      const res = await axios.post(`${API}/stayandplay/staff/checkin?pin=${staffPin}`, {
        account_id: posAccount.id, num_children: numChildren, num_adults: numAdults, payment_method: paymentMethod,
      });
      setCheckinResult(res.data);
      setPosStep("success");
      toast.success("Check-in complete!");
    } catch (err) { toast.error(err.response?.data?.detail || "Check-in failed"); }
  };

  const adjustWallet = async (isTopUp) => {
    const amt = parseFloat(walletAmount);
    if (isNaN(amt) || amt <= 0) { toast.error("Enter a valid amount"); return; }
    try {
      const res = await axios.post(`${API}/stayandplay/staff/wallet/adjust?pin=${staffPin}`, {
        account_id: posAccount.id, amount: isTopUp ? amt : -amt, description: walletDesc || undefined,
      });
      toast.success(`Wallet ${isTopUp ? 'topped up' : 'deducted'}: £${amt.toFixed(2)}`);
      setPosAccount(prev => ({ ...prev, wallet_balance: res.data.new_balance }));
      setWalletAmount(""); setWalletDesc(""); setShowWalletModal(false);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const saveSettings = async () => {
    try {
      await axios.put(`${API}/stayandplay/admin/settings?pin=${staffPin}`, settingsForm);
      setSettings(settingsForm);
      setShowSettingsModal(false);
      toast.success("Settings saved!");
    } catch { toast.error("Failed to save settings"); }
  };

  // ===== LOGIN =====
  if (!loggedIn) {
    return (
      <div className="min-h-screen bg-[#FFFBF5] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 shadow-lg max-w-sm w-full">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-[#ff3131]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-[#ff3131]" />
            </div>
            <h1 className="text-2xl font-bold text-[#2D3436]" style={{ fontFamily: 'Fredoka, cursive' }}>Staff Portal</h1>
            <p className="text-sm text-[#636E72] mt-1">Stay & Play Check-In</p>
          </div>
          <input type="password" maxLength={4} value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, ''))} onKeyDown={e => e.key === "Enter" && handleLogin()} placeholder="••••" className="w-full text-center text-3xl tracking-[0.5em] p-4 border-2 border-gray-100 rounded-xl focus:border-[#ff3131] outline-none mb-4" data-testid="sp-staff-pin" />
          <button onClick={handleLogin} className="w-full bg-[#ff3131] hover:bg-[#e62e2e] text-white py-3 rounded-full font-bold" style={{ fontFamily: 'Fredoka, cursive' }} data-testid="sp-staff-login-btn">Login</button>
        </div>
      </div>
    );
  }

  // ===== FULLSCREEN POS =====
  if (posAccount) {
    return (
      <div className="min-h-screen bg-[#f5f5f5] flex flex-col" data-testid="sp-pos-terminal">
        {/* POS Header */}
        <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#ff3131] text-white flex items-center justify-center font-bold text-sm" style={{ fontFamily: 'Fredoka, cursive' }}>
              {posAccount.full_name.split(" ").map(n => n[0]).join("")}
            </div>
            <div>
              <h2 className="font-bold text-[#2D3436] text-lg leading-tight" style={{ fontFamily: 'Fredoka, cursive' }}>{posAccount.full_name}</h2>
              <div className="flex items-center gap-3 text-xs text-[#636E72]">
                <span className="flex items-center gap-1"><Wallet className="w-3 h-3" />£{posAccount.wallet_balance?.toFixed(2)}</span>
                <span className="flex items-center gap-1"><Stamp className="w-3 h-3" />{posAccount.loyalty_stamps}/{settings.loyalty_target}</span>
                <span className="font-mono text-[10px]">{posAccount.qr_code}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowWalletModal(true)} className="p-2.5 bg-[#7dbff8]/10 text-[#7dbff8] rounded-xl hover:bg-[#7dbff8]/20"><Wallet className="w-5 h-5" /></button>
            <button onClick={toggleFullscreen} className="p-2.5 bg-gray-100 text-[#636E72] rounded-xl hover:bg-gray-200" title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"} data-testid="sp-pos-fullscreen-btn">
              {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </button>
            <button onClick={closePOS} className="p-2.5 bg-gray-100 text-[#636E72] rounded-xl hover:bg-gray-200" data-testid="sp-pos-close"><X className="w-5 h-5" /></button>
          </div>
        </header>

        {/* POS Body */}
        <div className="flex-1 flex items-center justify-center p-4">
          {/* SELECT STEP */}
          {posStep === "select" && (
            <div className="w-full max-w-lg">
              <h3 className="text-center text-xl font-bold text-[#2D3436] mb-8" style={{ fontFamily: 'Fredoka, cursive' }}>
                Who's attending today?
              </h3>

              {/* Children counter */}
              <div className="bg-white rounded-2xl p-6 mb-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-[#ff3131]/10 flex items-center justify-center">
                      <Baby className="w-6 h-6 text-[#ff3131]" />
                    </div>
                    <div>
                      <p className="font-bold text-[#2D3436] text-lg" style={{ fontFamily: 'Fredoka, cursive' }}>Children</p>
                      <p className="text-xs text-[#636E72]">£{settings.price_per_child.toFixed(2)} each</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button onClick={() => setNumChildren(Math.max(0, numChildren - 1))} className="w-12 h-12 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-[#2D3436] transition-colors" data-testid="sp-pos-child-minus">
                      <Minus className="w-5 h-5" />
                    </button>
                    <span className="text-3xl font-bold text-[#2D3436] w-10 text-center" style={{ fontFamily: 'Fredoka, cursive' }} data-testid="sp-pos-child-count">{numChildren}</span>
                    <button onClick={() => setNumChildren(numChildren + 1)} className="w-12 h-12 rounded-full bg-[#ff3131] hover:bg-[#e62e2e] flex items-center justify-center text-white transition-colors" data-testid="sp-pos-child-plus">
                      <Plus className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Adults counter */}
              <div className="bg-white rounded-2xl p-6 mb-6 shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-[#7dbff8]/10 flex items-center justify-center">
                      <User className="w-6 h-6 text-[#7dbff8]" />
                    </div>
                    <div>
                      <p className="font-bold text-[#2D3436] text-lg" style={{ fontFamily: 'Fredoka, cursive' }}>Adults</p>
                      <p className="text-xs text-[#636E72]">{settings.price_per_adult > 0 ? `£${settings.price_per_adult.toFixed(2)} each` : 'Free'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button onClick={() => setNumAdults(Math.max(0, numAdults - 1))} className="w-12 h-12 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-[#2D3436] transition-colors" data-testid="sp-pos-adult-minus">
                      <Minus className="w-5 h-5" />
                    </button>
                    <span className="text-3xl font-bold text-[#2D3436] w-10 text-center" style={{ fontFamily: 'Fredoka, cursive' }} data-testid="sp-pos-adult-count">{numAdults}</span>
                    <button onClick={() => setNumAdults(numAdults + 1)} className="w-12 h-12 rounded-full bg-[#7dbff8] hover:bg-[#6ca6d9] flex items-center justify-center text-white transition-colors" data-testid="sp-pos-adult-plus">
                      <Plus className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Continue button */}
              {(numChildren > 0 || numAdults > 0) && (
                <button onClick={() => setPosStep("confirm")} className="w-full bg-[#ff3131] hover:bg-[#e62e2e] text-white py-4 rounded-2xl font-bold text-xl flex items-center justify-center gap-2 transition-colors shadow-lg" style={{ fontFamily: 'Fredoka, cursive' }} data-testid="sp-pos-continue">
                  Continue — £{total.toFixed(2)}
                </button>
              )}
            </div>
          )}

          {/* CONFIRM STEP */}
          {posStep === "confirm" && (
            <div className="w-full max-w-lg">
              <h3 className="text-center text-xl font-bold text-[#2D3436] mb-6" style={{ fontFamily: 'Fredoka, cursive' }}>
                Payment Summary
              </h3>

              <div className="bg-white rounded-2xl p-6 shadow-sm mb-6">
                {/* Line items */}
                <div className="space-y-3 mb-4">
                  {numChildren > 0 && (
                    <div className="flex justify-between text-[#2D3436]">
                      <span>{numChildren} x Child{numChildren > 1 ? "ren" : ""}</span>
                      <span className="font-bold">£{(numChildren * settings.price_per_child).toFixed(2)}</span>
                    </div>
                  )}
                  {numAdults > 0 && settings.price_per_adult > 0 && (
                    <div className="flex justify-between text-[#2D3436]">
                      <span>{numAdults} x Adult{numAdults > 1 ? "s" : ""}</span>
                      <span className="font-bold">£{(numAdults * settings.price_per_adult).toFixed(2)}</span>
                    </div>
                  )}
                  {numAdults > 0 && !settings.price_per_adult && (
                    <div className="flex justify-between text-[#636E72]">
                      <span>{numAdults} x Adult{numAdults > 1 ? "s" : ""}</span>
                      <span>Free</span>
                    </div>
                  )}
                </div>

                <div className="border-t border-gray-100 pt-3 space-y-2">
                  <div className="flex justify-between text-lg">
                    <span className="text-[#636E72]">Subtotal</span>
                    <span className="font-bold text-[#2D3436]">£{total.toFixed(2)}</span>
                  </div>
                  {walletDeduction > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span className="flex items-center gap-1"><Wallet className="w-4 h-4" /> Wallet</span>
                      <span className="font-bold">-£{walletDeduction.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-2xl pt-2 border-t border-gray-200">
                    <span className="font-bold text-[#2D3436]" style={{ fontFamily: 'Fredoka, cursive' }}>To Pay</span>
                    <span className="font-bold text-[#ff3131]" style={{ fontFamily: 'Fredoka, cursive' }}>£{remaining.toFixed(2)}</span>
                  </div>
                </div>

                {/* Stamps preview */}
                <div className="mt-4 bg-[#FFFBF5] rounded-xl p-3 text-center">
                  <p className="text-sm text-[#636E72]">
                    <Stamp className="w-4 h-4 inline text-[#ff3131] mr-1" />
                    <strong className="text-[#ff3131]">+{numChildren}</strong> stamp{numChildren > 1 ? "s" : ""} will be awarded
                  </p>
                </div>
              </div>

              {/* Payment buttons */}
              <div className="space-y-3">
                {remaining === 0 ? (
                  <button onClick={() => doCheckin("wallet")} className="w-full bg-[#ff3131] hover:bg-[#e62e2e] text-white py-4 rounded-2xl font-bold text-xl flex items-center justify-center gap-2 transition-colors shadow-lg" style={{ fontFamily: 'Fredoka, cursive' }} data-testid="sp-pos-pay-wallet">
                    <Check className="w-6 h-6" /> Confirm (Wallet)
                  </button>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    <button onClick={() => doCheckin("card")} className="bg-[#ff3131] hover:bg-[#e62e2e] text-white py-4 rounded-2xl font-bold text-lg transition-colors shadow-lg" style={{ fontFamily: 'Fredoka, cursive' }} data-testid="sp-pos-pay-card">Card</button>
                    <button onClick={() => doCheckin("cash")} className="bg-[#2D3436] hover:bg-[#1a1f20] text-white py-4 rounded-2xl font-bold text-lg transition-colors shadow-lg" style={{ fontFamily: 'Fredoka, cursive' }} data-testid="sp-pos-pay-cash">Cash</button>
                  </div>
                )}
                <button onClick={() => setPosStep("select")} className="w-full text-[#636E72] hover:text-[#2D3436] py-2 text-sm font-semibold">Back</button>
              </div>
            </div>
          )}

          {/* SUCCESS STEP */}
          {posStep === "success" && checkinResult && (
            <div className="w-full max-w-lg text-center">
              <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-12 h-12 text-green-600" />
              </div>
              <h2 className="text-3xl font-bold text-[#2D3436] mb-2" style={{ fontFamily: 'Fredoka, cursive' }}>Checked In!</h2>
              <p className="text-[#636E72] mb-6">{posAccount.full_name}</p>

              <div className="bg-white rounded-2xl p-6 shadow-sm mb-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-[#FFFBF5] rounded-xl p-4">
                    <p className="text-xs text-[#636E72] mb-1">Total</p>
                    <p className="text-xl font-bold text-[#2D3436]" style={{ fontFamily: 'Fredoka, cursive' }}>£{checkinResult.total?.toFixed(2)}</p>
                  </div>
                  <div className="bg-[#FFFBF5] rounded-xl p-4">
                    <p className="text-xs text-[#636E72] mb-1">Stamps</p>
                    <p className="text-xl font-bold text-[#ff3131]" style={{ fontFamily: 'Fredoka, cursive' }}>+{checkinResult.stamps_awarded}</p>
                  </div>
                  {checkinResult.wallet_used > 0 && (
                    <div className="bg-[#FFFBF5] rounded-xl p-4">
                      <p className="text-xs text-[#636E72] mb-1">Wallet Used</p>
                      <p className="text-xl font-bold text-green-600" style={{ fontFamily: 'Fredoka, cursive' }}>£{checkinResult.wallet_used?.toFixed(2)}</p>
                    </div>
                  )}
                  <div className="bg-[#FFFBF5] rounded-xl p-4">
                    <p className="text-xs text-[#636E72] mb-1">New Balance</p>
                    <p className="text-xl font-bold text-[#7dbff8]" style={{ fontFamily: 'Fredoka, cursive' }}>£{checkinResult.new_wallet_balance?.toFixed(2)}</p>
                  </div>
                </div>
                {checkinResult.rewards_earned > 0 && (
                  <div className="mt-4 bg-[#ff3131]/10 rounded-xl p-4 border-2 border-[#ff3131]">
                    <p className="text-lg font-bold text-[#ff3131]" style={{ fontFamily: 'Fredoka, cursive' }}>
                      LOYALTY REWARD! +£{checkinResult.reward_balance_added?.toFixed(2)} added!
                    </p>
                  </div>
                )}
              </div>

              <button onClick={closePOS} className="bg-[#ff3131] hover:bg-[#e62e2e] text-white px-12 py-4 rounded-2xl font-bold text-xl transition-colors shadow-lg" style={{ fontFamily: 'Fredoka, cursive' }} data-testid="sp-pos-done">
                Done
              </button>
            </div>
          )}
        </div>

        {/* Wallet Modal */}
        {showWalletModal && (
          <div className="fixed inset-0 z-[200] bg-black/50 flex items-center justify-center p-4" onClick={() => setShowWalletModal(false)}>
            <div className="bg-white rounded-2xl p-6 max-w-sm w-full" onClick={e => e.stopPropagation()}>
              <h3 className="font-bold text-lg text-[#2D3436] mb-4" style={{ fontFamily: 'Fredoka, cursive' }}>Adjust Wallet</h3>
              <p className="text-sm text-[#636E72] mb-3">Current: <strong>£{posAccount?.wallet_balance?.toFixed(2)}</strong></p>
              <input type="number" step="0.01" min="0" value={walletAmount} onChange={e => setWalletAmount(e.target.value)} placeholder="Amount (£)" className="w-full p-3 border-2 border-gray-100 rounded-xl focus:border-[#ff3131] outline-none mb-3" />
              <input type="text" value={walletDesc} onChange={e => setWalletDesc(e.target.value)} placeholder="Description (optional)" className="w-full p-3 border-2 border-gray-100 rounded-xl focus:border-[#ff3131] outline-none mb-4" />
              <div className="flex gap-2">
                <button onClick={() => adjustWallet(true)} className="flex-1 bg-green-500 hover:bg-green-600 text-white py-3 rounded-full font-bold text-sm">Top Up</button>
                <button onClick={() => adjustWallet(false)} className="flex-1 bg-red-500 hover:bg-red-600 text-white py-3 rounded-full font-bold text-sm">Deduct</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ===== SCAN / SEARCH MODE =====
  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="sp-staff-portal">
      <header className="bg-white border-b border-gray-100 px-4 py-3 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Users className="w-5 h-5 text-[#ff3131]" />
            <h1 className="text-lg font-bold text-[#2D3436]" style={{ fontFamily: 'Fredoka, cursive' }}>Stay & Play</h1>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={toggleFullscreen} className="p-2 text-[#636E72] hover:text-[#2D3436]" title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"} data-testid="sp-staff-fullscreen-btn">
              {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </button>
            <button onClick={() => { setShowSettingsModal(true); setSettingsForm(settings); }} className="p-2 text-[#636E72] hover:text-[#2D3436]"><Settings className="w-5 h-5" /></button>
            <button onClick={() => setLoggedIn(false)} className="p-2 text-[#636E72] hover:text-[#ff3131]"><LogOut className="w-4 h-4" /></button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-4">
        {/* QR Scanner */}
        <div className="bg-white rounded-2xl p-4 shadow-sm mb-4" data-testid="sp-qr-scanner-container">
          <div className="flex items-center gap-2 mb-3">
            <Camera className="w-5 h-5 text-[#ff3131]" />
            <span className="text-sm font-bold text-[#2D3436]" style={{ fontFamily: 'Fredoka, cursive' }}>Scan QR Code</span>
          </div>
          <div className="relative mx-auto overflow-hidden rounded-xl bg-black" style={{ width: '100%', maxWidth: '320px', aspectRatio: '1' }}>
            <div id="sp-qr-reader" style={{ width: '100%', height: '100%' }} />
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              <div className="w-48 h-48 border-2 border-white/40 rounded-xl" />
            </div>
          </div>
          <p className="text-xs text-[#636E72] text-center mt-2">Point camera at member's QR code</p>
        </div>

        {/* Search */}
        <div className="bg-white rounded-2xl p-4 shadow-sm mb-4">
          <div className="flex items-center gap-2 mb-2">
            <Search className="w-4 h-4 text-[#636E72]" />
            <span className="text-xs font-semibold text-[#636E72]">Or search manually</span>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#636E72]" />
            <input type="text" value={searchQuery} onChange={e => search(e.target.value)} placeholder="Name, email, mobile..." className="w-full pl-11 pr-4 py-3.5 border-2 border-gray-100 rounded-xl focus:border-[#ff3131] outline-none text-lg" data-testid="sp-staff-search" />
          </div>
          {searchResults.length > 0 && (
            <div className="mt-2 space-y-1">
              {searchResults.map(a => (
                <button key={a.id} onClick={() => openPOS(a.id)} className="w-full text-left flex items-center justify-between bg-[#FFFBF5] rounded-xl p-3 hover:bg-[#ff3131]/5 transition-colors" data-testid={`sp-staff-result-${a.id}`}>
                  <div>
                    <p className="font-semibold text-[#2D3436]">{a.full_name}</p>
                    <p className="text-xs text-[#636E72]">{a.email} · {a.mobile}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-mono text-[#636E72]">{a.qr_code}</p>
                    <p className="text-xs text-[#ff3131] font-semibold">£{a.wallet_balance?.toFixed(2)} · {a.loyalty_stamps} stamps</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Settings Modal */}
      {showSettingsModal && (
        <div className="fixed inset-0 z-[200] bg-black/50 flex items-center justify-center p-4" onClick={() => setShowSettingsModal(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-lg text-[#2D3436] mb-4" style={{ fontFamily: 'Fredoka, cursive' }}>Settings</h3>
            <div className="space-y-3 mb-4">
              <div>
                <label className="block text-xs font-semibold text-[#2D3436] mb-1">Price per child (£)</label>
                <input type="number" step="0.50" value={settingsForm.price_per_child || ""} onChange={e => setSettingsForm(p => ({ ...p, price_per_child: parseFloat(e.target.value) || 0 }))} className="w-full p-2.5 border-2 border-gray-100 rounded-xl outline-none" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#2D3436] mb-1">Price per adult (£)</label>
                <input type="number" step="0.50" value={settingsForm.price_per_adult || ""} onChange={e => setSettingsForm(p => ({ ...p, price_per_adult: parseFloat(e.target.value) || 0 }))} className="w-full p-2.5 border-2 border-gray-100 rounded-xl outline-none" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#2D3436] mb-1">Stamps to earn reward</label>
                <input type="number" value={settingsForm.loyalty_target || ""} onChange={e => setSettingsForm(p => ({ ...p, loyalty_target: parseInt(e.target.value) || 0 }))} className="w-full p-2.5 border-2 border-gray-100 rounded-xl outline-none" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#2D3436] mb-1">Reward value (£)</label>
                <input type="number" step="0.50" value={settingsForm.reward_value || ""} onChange={e => setSettingsForm(p => ({ ...p, reward_value: parseFloat(e.target.value) || 0 }))} className="w-full p-2.5 border-2 border-gray-100 rounded-xl outline-none" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#2D3436] mb-1">After reward</label>
                <select value={settingsForm.reward_behaviour || "reset"} onChange={e => setSettingsForm(p => ({ ...p, reward_behaviour: e.target.value }))} className="w-full p-2.5 border-2 border-gray-100 rounded-xl outline-none">
                  <option value="reset">Reset stamps to 0</option>
                  <option value="rollover">Rollover extra stamps</option>
                </select>
              </div>
            </div>
            <button onClick={saveSettings} className="w-full bg-[#ff3131] hover:bg-[#e62e2e] text-white py-3 rounded-full font-bold" style={{ fontFamily: 'Fredoka, cursive' }}>Save Settings</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SPStaffPortal;
